import { useState, useEffect, useCallback, useId } from "react";
import useDropdownStore from '@/stores/dropdownStore';

function useDropdownField(items = [], searchKey = "name", displayKey = "name", getDisplayValue) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const dropdownId = useId();
  const { openDropdownId, setOpenDropdown, closeAllDropdowns } = useDropdownStore();
  
  const isOpen = openDropdownId === dropdownId;
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(`[data-dropdown-id="${dropdownId}"]`)) {
        closeAllDropdowns();
      }
    };
    
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen, dropdownId, closeAllDropdowns]);

  const filteredItems = (Array.isArray(items) ? items : []).filter((item) => {
    const searchValue = item[searchKey]?.toLowerCase() || "";
    const displayValue = item[displayKey]?.toLowerCase() || "";
    const term = searchTerm.toLowerCase();

    // Búsqueda básica por campos individuales
    if (searchValue.includes(term) || displayValue.includes(term)) {
      return true;
    }

    // Búsqueda especial para contactos (FirstName + LastName)
    if (item.FirstName && item.LastName) {
      const fullName = `${item.FirstName} ${item.LastName}`.toLowerCase();
      if (fullName.includes(term)) {
        return true;
      }
    }

    return false;
  });

  const handleSelect = useCallback(
    (item) => {
      setSelectedItem(item);
      const displayText = getDisplayValue ? getDisplayValue(item) : (item[displayKey] || item[searchKey] || "");
      setSearchTerm(displayText);
      closeAllDropdowns();
    },
    [displayKey, searchKey, getDisplayValue, closeAllDropdowns]
  );

  const handleInputChange = useCallback((value) => {
    setSearchTerm(value);
    setOpenDropdown(dropdownId);
  }, [dropdownId, setOpenDropdown]);

  const handleInputFocus = useCallback(() => {
    setOpenDropdown(dropdownId);
  }, [dropdownId, setOpenDropdown]);

  const handleInputClick = useCallback(() => {
    setOpenDropdown(dropdownId);
  }, [dropdownId, setOpenDropdown]);

  const clearSelection = useCallback(() => {
    setSelectedItem(null);
    setSearchTerm("");
    closeAllDropdowns();
  }, [closeAllDropdowns]);



  return {
    searchTerm,
    isOpen,
    selectedItem,
    filteredItems,
    handleSelect,
    handleInputChange,
    handleInputFocus,
    handleInputClick,
    clearSelection,
    setSearchTerm,
    dropdownId,
  };
}

export default useDropdownField;
